(ns client-portal.client.reports.components.table
  (:require [clojure.string :as s]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.reports.components.ai-summary :as ai-summary]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.utils.currency :as currency]))

(defn- format-cell
  "Render one cell value using the header's optional `value-type`: currency/percent/
   number format the number; location/concept map a raw id to its name via
   `id->name` (a map of value-type -> {id-string name}); anything else is shown
   as-is, with a dash for missing values."
  [value value-type country-code id->name]
  (cond
    (nil? value) "—"
    (= value-type "currency") (currency/currency-integer->readable-currency (or country-code "US") value)
    (= value-type "percent") (str (util/to-precision-num (* value 100) :precision 2) "%")
    (= value-type "number") (util/readable-number value)
    (contains? id->name value-type) (get (get id->name value-type) (str value) (str value))
    :else (str value)))

(defn- table-cell
  "A formatted cell, tinted by the header's `goals` ({:stops :colors}, see
   `util/stop-color`) and given an up/down/same arrow from its `trend-key` column —
   \"up\"/\"down\"/\"same\", or a number whose sign is the direction. `trend-colors?`
   false draws the arrow in the cell's text color instead of green/red. A
   `trend-value-key` column's value (what the trend compares against) follows the
   arrow in parentheses."
  [row {:keys [key value-type goals trend-key trend-colors? trend-value-key]} country-code id->name]
  (let [value (get row key)
        label (format-cell value value-type country-code id->name)
        trend-value (some-> trend-value-key
                            (->> (get row))
                            (format-cell value-type country-code id->name))
        goal-color (when goals
                     (util/stop-color value (:stops goals) (:colors goals)))
        trend (let [t (some->> trend-key
                               (get row))]
                (cond
                  (number? t) (cond
                                (pos? t) :up
                                (neg? t) :down
                                :else :same)
                  (string? t) (-> t
                                  (s/lower-case)
                                  (keyword))))
        text-color (style/alc-color "Text/primary")
        colors? (not (false? trend-colors?))
        [trend-icon trend-color] (case trend
                                   :up [:arrow-upward (if colors? (style/alc-color "Common/success_hex") text-color)]
                                   :down [:arrow-downward (if colors? (style/alc-color "Background/error_const") text-color)]
                                   :same [:remove text-color]
                                   nil)]
    (if (or goal-color trend-icon)
      {:label [:div {:style (cond-> {:display :inline-flex
                                     :align-items :center
                                     :gap 2}
                              goal-color
                              (merge {:background-color (util/hex->rgba goal-color 0.3)
                                      :border-radius 4
                                      :padding "0 6px"
                                      :margin-left -6}))}
               [:span label]
               (when trend-icon
                 [alc/material-icon {:icon-type trend-icon
                                     :style {:color trend-color
                                             :font-size 14}}])
               (when trend-value
                 [:span {:style {:color (style/alc-color "Text/secondary")}}
                  (str "(" trend-value ")")])]}
      label)))

(defn- ->string-keyed
  "A {id name} lookup with string keys, so cell values (numbers or strings) match."
  [id->name]
  (into {} (map (fn [[id nm]] [(str id) nm])) id->name))

(defn table
  "Report table component: renders the query-injected `data` (a seq of string-keyed
   rows) as an amelia-light table, one column per `headers` entry. Each header is
   `{:key <query-column> :label <header> :value-type <optional> :goals <optional>
   :trend-key <optional>}` (see `table-cell`). A `:value-type` of \"location\" or
   \"concept\" maps that column's raw id to its name. Goals get a legend of their bands
   above the table (unless `:show-legend?` is false), labeled by `:labels` or else each band's
   range. Wrapped in a card so it can
   carry a title/description. A `:trend-period` notes what the trends compare against."
  [{:keys [title description headers data country-code ai-summary trend-period]}]
  (let [location-id->name @(re/subscribe [:locations/location-id->label-by-current-concept-id-])
        concepts (util/when-not-loading @(re/subscribe [:concepts/list-data-]))
        id->name {"location" (->string-keyed location-id->name)
                  "concept" (into {}
                                  (keep (fn [{:keys [id label] :as concept}]
                                          (when (and id label)
                                            [(str id) (anon.d/concept-label concept)])))
                                  concepts)}
        loading? (util/loading? data)
        rows (when-not loading? data)
        headers (mapv util/->kebab-case-keywords
                      (or headers
                          (some->> data
                                   (first)
                                   (keys)
                                   (mapv (fn [k] {:key k :label (util/->capitalize-words k)})))))]
    [alc/card {:title title
               :subtitle description
               :style {:height "100%"}}
     [ai-summary/ai-summary {:summary ai-summary}]
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 12}}
      (when-let [legends (->> headers
                              (filter #(and (:goals %)
                                            (not (false? (-> % :goals :show-legend?)))))
                              (not-empty))]
        (into [:div {:style {:display :flex
                             :flex-direction :column
                             :gap 6}}]
              (map (fn [{:keys [label value-type] {:keys [stops colors labels]} :goals}]
                     (into [:div {:style {:display :flex
                                          :flex-wrap :wrap
                                          :align-items :center
                                          :column-gap 12
                                          :row-gap 4
                                          :font-size 12
                                          :color (style/alc-color "Text/secondary")}}
                            [:span {:style {:color (style/alc-color "Text/primary")}}
                             label]]
                           (map (fn [color band-label]
                                  [:div {:style {:display :flex
                                                 :align-items :center
                                                 :gap 6}}
                                   [:span {:style {:width 10
                                                   :height 10
                                                   :border-radius 2
                                                   :background-color (util/hex->rgba color 0.3)
                                                   :border (str "1px solid " color)}}]
                                   band-label])
                                colors
                                (util/stop-labels stops labels #(format-cell % value-type country-code id->name)))))
                   legends)))
      [alc/table
       {:container-loading? loading?
        :flex-vals (mapv (constantly "1 0 96px") headers)
        :header-style {:min-width :fit-content}
        :row-style {:min-width :fit-content}
        :headers (mapv (fn [{:keys [label]}] {:label label}) headers)
        :rows (mapv (fn [row]
                      {:cells (mapv #(table-cell row % country-code id->name) headers)})
                    rows)}]
      (when (and trend-period (not-empty rows))
        [:div {:style {:color (style/alc-color "Text/secondary")
                       :font-size 13}}
         [:span {:style {:font-weight style/font-medium-weight}}
          (str "Trend Comparison (vs. " trend-period ")")]
         " · prior period values in parentheses"])
      (when (not-empty rows)
        [:div {:data-html2canvas-ignore "true"
               :style {:display :flex
                       :justify-content :flex-end}}
         [alc/download-csv-button {:title title
                                   :headers (mapv :label headers)
                                   :rows (mapv (fn [row]
                                                 (mapv (fn [{:keys [key value-type]}]
                                                         (let [v (get row key)]
                                                           (if (contains? id->name value-type)
                                                             (format-cell v value-type country-code id->name)
                                                             v)))
                                                       headers))
                                               rows)}]])]]))
